<!-- components/auth/RegisterStepForm.vue -->
<template>
  <div class="min-h-screen relative overflow-hidden">

    <!-- ── Background identical to login ── -->
    <div class="absolute inset-0 bg-gradient-to-br from-card-dark via-[#15181c] to-background-dark"></div>

    <div class="absolute inset-0 opacity-[0.06]"
      style="background: linear-gradient(135deg, #8A8A8A 0%, #D4D4D4 50%, #9A9A9A 100%);">
    </div>

    <div class="absolute inset-0 opacity-[0.18]"
      style="background-image: radial-gradient(circle at 1px 1px, rgba(17,24,39,0.08) 1px, transparent 0); background-size: 22px 22px;">
    </div>

    <!-- Glow blobs -->
    <div class="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl opacity-30"
      style="background: radial-gradient(circle, rgba(11,129,239,0.55), transparent 60%);">
    </div>
    <div class="pointer-events-none absolute -bottom-28 -right-28 h-80 w-80 rounded-full blur-3xl opacity-25"
      style="background: radial-gradient(circle, rgba(34,197,94,0.40), transparent 60%);">
    </div>

    <!-- ── Content ── -->
    <div class="relative min-h-screen flex flex-col items-center justify-center px-4 py-10 gap-4">

      <!-- 14-day free trial banner -->
      <div class="w-full max-w-2xl">
        <div
          class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-blue-600 to-indigo-700 p-5 text-white shadow-xl">
          <div class="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl">
          </div>
          <div class="pointer-events-none absolute -left-6 -bottom-8 h-28 w-28 rounded-full bg-white/10 blur-xl">
          </div>

          <div class="relative flex items-center gap-4">
            <div
              class="flex h-13 w-13 flex-shrink-0 items-center justify-center rounded-xl bg-white/20 p-2.5 shadow-inner">
              <span class="material-symbols-outlined text-2xl">workspace_premium</span>
            </div>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-[10px] font-bold uppercase tracking-widest text-white/70">Prueba gratuita</span>
                <span
                  class="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold backdrop-blur-sm">
                  <span class="material-symbols-outlined text-xs">credit_card_off</span>
                  Sin tarjeta requerida
                </span>
              </div>
              <p class="mt-0.5 text-lg font-extrabold leading-snug tracking-tight">
                14 días gratis, sin compromisos
              </p>
              <p class="mt-0.5 text-xs text-white/75">
                Acceso completo a todas las funciones. Cancela cuando quieras.
              </p>
            </div>
          </div>

          <div class="relative mt-4 flex flex-wrap gap-2">
            <span v-for="feat in ['IA', 'Inventario', 'Reportes financieros', 'Soporte incluido']"
              :key="feat"
              class="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold backdrop-blur-sm">
              <span class="material-symbols-outlined text-xs text-emerald-300">check_circle</span>
              {{ feat }}
            </span>
          </div>
        </div>
      </div>

      <!-- ── Form card (matches login glassmorphism) ── -->
      <div class="w-full max-w-2xl relative rounded-[28px] border border-white/10
                  bg-white/70 dark:bg-white/[0.06]
                  backdrop-blur-2xl shadow-[0_30px_90px_rgba(0,0,0,0.45)] overflow-hidden">

        <!-- Apple-style top highlight — matches AuthForm -->
        <div class="pointer-events-none absolute inset-0 rounded-[28px] overflow-hidden">
          <div class="absolute inset-0 opacity-[0.9]" style="background:
            radial-gradient(circle at 30% 18%, rgba(255,255,255,0.55), transparent 55%),
            radial-gradient(circle at 80% 80%, rgba(255,255,255,0.10), transparent 60%),
            linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0.04));">
          </div>
        </div>

        <div class="relative">
          <!-- Header -->
          <div class="px-6 sm:px-8 pt-8 pb-6 border-b border-black/[0.06] dark:border-white/10">

            <!-- Logo + title row -->
            <div class="flex flex-col items-center text-center gap-3 mb-5">
              <img :src="finovaLogo" alt="Finova"
                   class="h-14 w-auto select-none pointer-events-none rounded-2xl shadow-md" />
              <div>
                <h1 class="text-xl sm:text-2xl font-extrabold tracking-tight
                           text-silver-900 dark:text-white leading-tight">
                  Crear cuenta
                </h1>
                <p class="mt-1 text-xs sm:text-sm text-silver-700 dark:text-white/50">
                  Completa tu registro en pocos pasos.
                </p>
              </div>
            </div>

            <!-- Step indicator + progress -->
            <div class="flex items-center justify-between gap-3 mb-4">
              <span class="text-xs font-semibold text-silver-500 dark:text-white/40">
                <template v-if="phase === 'verify_email'">Verificación de correo</template>
                <template v-else>Paso {{ step }} / {{ totalSteps }}</template>
              </span>
              <span class="inline-flex items-center justify-center rounded-full h-8 px-3 text-xs font-bold
                           bg-black/[0.06] dark:bg-white/10 text-silver-800 dark:text-white/80">
                {{ stepTitle }}
              </span>
            </div>

            <div class="h-1.5 w-full rounded-full bg-black/[0.08] dark:bg-white/10 overflow-hidden">
              <div class="h-full bg-primary transition-all duration-500" :style="{ width: `${progressPct}%` }"></div>
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 sm:px-8 py-6">
            <!-- Global error -->
            <div v-if="errorMessage"
              class="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300 backdrop-blur-sm">
              {{ errorMessage }}
            </div>

            <!-- ── EMAIL VERIFICATION PHASE ── -->
            <transition name="fade" mode="out-in">
              <div v-if="phase === 'verify_email'" key="verify-phase" class="flex flex-col items-center gap-6 py-4">

                <!-- Icon -->
                <div class="size-16 rounded-2xl flex items-center justify-center shadow-lg shrink-0"
                     style="background:linear-gradient(135deg,#3b82f6,#6366f1);">
                  <span class="material-symbols-outlined text-white text-[30px]">mark_email_unread</span>
                </div>

                <div class="text-center">
                  <p class="text-base font-bold text-silver-900 dark:text-white">Revisa tu correo</p>
                  <p class="mt-1 text-sm text-silver-700 dark:text-white/50 leading-relaxed">
                    Enviamos un código de 6 dígitos a<br />
                    <strong class="text-silver-800 dark:text-white/80">{{ maskedEmail }}</strong>
                  </p>
                </div>

                <!-- 6-digit code input -->
                <div class="w-full max-w-xs flex flex-col gap-2">
                  <label class="flex flex-col gap-1.5">
                    <span class="text-xs font-bold text-silver-800 dark:text-white/70 text-center">Código de verificación</span>
                    <input
                      ref="codeInput"
                      v-model.trim="verificationCode"
                      type="text"
                      inputmode="numeric"
                      maxlength="6"
                      class="main-input text-center text-2xl font-bold tracking-[0.4em]"
                      :class="{ 'error': errors.code }"
                      placeholder="000000"
                      @keydown.enter.prevent="submitCode"
                    />
                    <span v-if="errors.code" class="text-xs text-red-400 text-center">{{ errors.code }}</span>
                  </label>

                  <p class="text-[11px] text-silver-500 dark:text-white/30 text-center">
                    El código expira en 10 minutos.
                  </p>
                </div>

                <!-- Resend -->
                <p class="text-xs text-silver-500 dark:text-white/30">
                  ¿No llegó?
                  <button type="button"
                    :disabled="resendCooldown > 0 || loading"
                    @click="resendCode"
                    class="text-blue-500 dark:text-blue-400 hover:underline font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                    {{ resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar código' }}
                  </button>
                </p>
              </div>

              <!-- STEP 1: Cuenta -->
              <div v-else-if="step === 1" key="step-1" class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div class="sm:col-span-2">
                  <p class="text-xs font-bold tracking-widest text-silver-500 dark:text-white/40 uppercase">Datos de acceso</p>
                  <p class="mt-1 text-sm text-silver-700 dark:text-white/60">
                    Usa un correo válido. Tu username será tu identificador.
                  </p>
                </div>

                <label class="flex flex-col gap-1.5">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Nombre *</span>
                  <input v-model.trim="form.first_name" type="text" class="main-input"
                    :class="{ 'error': errors.first_name }" placeholder="Tu nombre" />
                  <span v-if="errors.first_name" class="text-xs text-red-400">{{ errors.first_name }}</span>
                </label>

                <label class="flex flex-col gap-1.5">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Apellido *</span>
                  <input v-model.trim="form.last_name" type="text" class="main-input"
                    :class="{ 'error': errors.last_name }" placeholder="Tu apellido" />
                  <span v-if="errors.last_name" class="text-xs text-red-400">{{ errors.last_name }}</span>
                </label>

                <label class="flex flex-col gap-1.5 sm:col-span-2">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Correo electrónico *</span>
                  <input v-model.trim="form.email" type="email" class="main-input"
                    :class="{ 'error': errors.email }" placeholder="correo@empresa.com" />
                  <span v-if="errors.email" class="text-xs text-red-400">{{ errors.email }}</span>
                </label>

                <label class="flex flex-col gap-1.5">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Username *</span>
                  <input v-model.trim="form.username" type="text" class="main-input"
                    :class="{ 'error': errors.username }" placeholder="ej: finova.admin" autocomplete="username" />
                  <span v-if="errors.username" class="text-xs text-red-400">{{ errors.username }}</span>
                </label>

                <label class="flex flex-col gap-1.5">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Contraseña *</span>
                  <div class="relative">
                    <input v-model="form.password" :type="showPassword ? 'text' : 'password'"
                      class="main-input pr-11" :class="{ 'error': errors.password }" placeholder="••••••••"
                      autocomplete="new-password" />
                    <button type="button"
                      class="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
                      @click="showPassword = !showPassword"
                      :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'">
                      <span class="material-symbols-outlined text-base">
                        {{ showPassword ? 'visibility_off' : 'visibility' }}
                      </span>
                    </button>
                  </div>
                  <span v-if="errors.password" class="text-xs text-red-400">{{ errors.password }}</span>
                  <p class="text-[11px] text-silver-500 dark:text-white/35">Mínimo 8 caracteres. Recomendado: letras + números.</p>
                </label>

                <label class="flex flex-col gap-1.5 sm:col-span-2">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Teléfono *</span>
                  <input v-model.trim="form.phone" type="tel" class="main-input"
                    :class="{ 'error': errors.phone }" placeholder="+52 1 656 000 0000" />
                  <span v-if="errors.phone" class="text-xs text-red-400">{{ errors.phone }}</span>
                </label>

                <!-- Cloudflare Turnstile CAPTCHA -->
                <div class="sm:col-span-2 flex flex-col items-center gap-2 pt-1">
                  <div ref="turnstileEl"></div>
                  <span v-if="errors.captcha" class="text-xs text-red-400">{{ errors.captcha }}</span>
                </div>
              </div>

              <!-- STEP 2: Perfil de negocio -->
              <div v-else-if="step === 2" key="step-2" class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div class="sm:col-span-2">
                  <p class="text-xs font-bold tracking-widest text-silver-500 dark:text-white/40 uppercase">Sobre tu negocio</p>
                  <p class="mt-1 text-sm text-silver-700 dark:text-white/60">Esto nos ayuda a ajustar la experiencia y reportes.</p>
                </div>

                <label class="flex flex-col gap-1.5 sm:col-span-2">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Sector *</span>
                  <select v-model="form.focus_sector" class="main-input" :class="{ 'error': errors.focus_sector }">
                    <option value="" disabled>Selecciona un sector…</option>
                    <option v-for="s in SECTORS" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <span v-if="errors.focus_sector" class="text-xs text-red-400">{{ errors.focus_sector }}</span>
                </label>

                <label class="flex flex-col gap-1.5 sm:col-span-2">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Tu cargo *</span>
                  <select v-model="form.role_in_business" class="main-input"
                    :class="{ 'error': errors.role_in_business }">
                    <option value="" disabled>Selecciona tu cargo…</option>
                    <option v-for="r in ROLES" :key="r" :value="r">{{ r }}</option>
                  </select>
                  <span v-if="errors.role_in_business" class="text-xs text-red-400">{{ errors.role_in_business
                  }}</span>
                  <p class="text-[11px] text-silver-500 dark:text-white/35">Si tu cargo no aparece, elige "Otro / No especificado".</p>
                </label>
              </div>

              <!-- STEP 3: Empresa -->
              <div v-else key="step-3" class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div class="sm:col-span-2 flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-xs font-bold tracking-widest text-silver-500 dark:text-white/40 uppercase">Empresa</p>
                    <p class="mt-1 text-sm text-silver-700 dark:text-white/60">Puedes editar esto después en Configuración.</p>
                  </div>
                  <span class="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold
                               bg-black/[0.06] dark:bg-white/10 text-silver-700 dark:text-white/60">
                    Opcional
                  </span>
                </div>

                <label class="flex flex-col gap-1.5 sm:col-span-2">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Nombre del negocio *</span>
                  <input v-model.trim="company.name" type="text" class="main-input"
                    :class="{ 'error': errors.company_name }" placeholder="Ej. Finova" />
                  <span v-if="errors.company_name" class="text-xs text-red-400">{{ errors.company_name }}</span>
                </label>

                <label class="flex flex-col gap-1.5 sm:col-span-2">
                  <span class="text-xs font-bold text-silver-800 dark:text-white/70">Razón social *</span>
                  <input v-model.trim="company.legal_name" type="text" class="main-input"
                    :class="{ 'error': errors.company_legal_name }" placeholder="Ej. Finova S.A. de C.V." />
                  <span v-if="errors.company_legal_name" class="text-xs text-red-400">{{ errors.company_legal_name }}</span>
                </label>
              </div>
            </transition>

            <!-- Footer actions -->
            <div class="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">

              <!-- Back button — hidden during verify phase -->
              <button v-if="phase !== 'verify_email'" type="button"
                class="rounded-xl px-4 py-2.5 text-sm font-semibold
                       bg-black/[0.06] dark:bg-white/10
                       text-silver-800 dark:text-white/80
                       hover:bg-black/10 dark:hover:bg-white/15
                       transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="loading || step === 1" @click="prevStep">
                <span class="inline-flex items-center gap-2">
                  <span class="material-symbols-outlined text-base">arrow_back</span>
                  Atrás
                </span>
              </button>

              <!-- Back to step 1 during verify phase -->
              <button v-else type="button"
                class="rounded-xl px-4 py-2.5 text-sm font-semibold
                       bg-black/[0.06] dark:bg-white/10
                       text-silver-800 dark:text-white/80
                       hover:bg-black/10 dark:hover:bg-white/15
                       transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="loading" @click="backToStep1">
                <span class="inline-flex items-center gap-2">
                  <span class="material-symbols-outlined text-base">arrow_back</span>
                  Cambiar datos
                </span>
              </button>

              <div class="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end w-full sm:w-auto">
                <a class="rounded-xl px-4 py-2.5 text-sm font-semibold text-center
                          border border-black/10 dark:border-white/10
                          text-silver-700 dark:text-white/60
                          hover:bg-black/5 dark:hover:bg-white/5
                          hover:text-silver-800 dark:hover:text-white/80 transition-colors"
                  href="/login">
                  Ya tengo cuenta
                </a>

                <!-- Verify code button -->
                <button v-if="phase === 'verify_email'" type="button"
                  class="rounded-xl px-5 py-2.5 text-sm font-semibold bg-primary text-white
                         hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  :disabled="loading || verificationCode.length !== 6" @click="submitCode">
                  <span v-if="!loading" class="inline-flex items-center gap-2">
                    Verificar código
                    <span class="material-symbols-outlined text-base">verified</span>
                  </span>
                  <span v-else class="inline-flex items-center gap-2">
                    <span class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                    Verificando…
                  </span>
                </button>

                <!-- Normal next / submit button -->
                <button v-else type="button"
                  class="rounded-xl px-5 py-2.5 text-sm font-semibold bg-primary text-white
                         hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  :disabled="loading" @click="nextOrSubmit">
                  <span v-if="!loading" class="inline-flex items-center gap-2">
                    {{ step < totalSteps ? "Continuar" : "Crear cuenta" }}
                    <span class="material-symbols-outlined text-base">arrow_forward</span>
                  </span>
                  <span v-else class="inline-flex items-center gap-2">
                    <span class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                    {{ step === 1 ? 'Enviando código…' : 'Guardando…' }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Version pill -->
      <div class="select-none" title="Versión de la app">
        <div
          class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs font-bold bg-white/10 backdrop-blur text-white/80 shadow-sm">
          <span class="material-symbols-outlined text-base opacity-80">deployed_code</span>
          <span>{{ currentVersion }}</span>
        </div>
      </div>

      <!-- Footer microcopy -->
      <p class="text-[11px] text-white/40">Finanzas empresariales claras, inteligentes y automáticas.</p>

    </div>
  </div>
</template>

<script>
import { normalizeApiError } from "@helpers/api_errors";
import { currentVersionString } from "@helpers/app_version";

const SECTORS = Object.freeze([
  "Tecnología / Software",
  "Servicios profesionales",
  "Comercio (retail)",
  "E-commerce",
  "Restaurantes / Alimentos",
  "Salud / Clínicas",
  "Educación",
  "Construcción",
  "Manufactura",
  "Logística / Transporte",
  "Automotriz",
  "Inmobiliaria",
  "Turismo / Hospitalidad",
  "Finanzas / Seguros",
  "Marketing / Publicidad",
  "Gubernamental / ONG",
  "Otro",
]);

const ROLES = Object.freeze([
  "Dueño(a) / Socio(a)",
  "Director(a) General (CEO)",
  "Director(a) de Operaciones (COO)",
  "Director(a) Financiero (CFO)",
  "Director(a) Comercial / Ventas",
  "Director(a) de Marketing",
  "Director(a) de Tecnología (CTO)",
  "Gerente General",
  "Gerente de Operaciones",
  "Gerente de Finanzas / Contabilidad",
  "Gerente de Ventas",
  "Gerente de Marketing",
  "Administrador(a)",
  "Contador(a)",
  "Analista",
  "Asistente",
  "Freelancer / Consultor(a)",
  "Otro / No especificado",
]);

const USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;

export function validateUsername(username) {
  const v = String(username || "").trim();

  if (!v) return "El usuario es requerido.";
  if (v.length < 3) return "El usuario debe tener al menos 3 caracteres.";
  if (v.length > 30) return "El usuario no debe exceder 30 caracteres.";
  if (!USERNAME_REGEX.test(v)) {
    return "Solo se permiten letras, números, guiones (-) y guiones bajos (_).";
  }

  return null;
}

export function normalizeUsername(username) {
  return String(username || "")
    .trim()
    .replace(/\s+/g, "");
}

export default {
  name: "RegisterStepForm",
  inject: ["$axios", "toast"],
  emits: ["registered", "goLogin"],

  props: {
    registerUrl: { type: String, default: "/register" },
  },

  data() {
    return {
      finovaLogo: '/finova-logo.jpeg',
      step: 1,
      totalSteps: 3,
      loading: false,
      showPassword: false,
      errorMessage: null,

      // Email verification phase
      phase: 'form',          // 'form' | 'verify_email'
      verificationCode: '',
      verifiedToken: null,
      resendCooldown: 0,
      _resendTimer: null,

      // Turnstile CAPTCHA
      captchaToken: null,
      _turnstileId: null,

      SECTORS,
      ROLES,

      form: {
        first_name: "",
        last_name: "",
        email: "",
        username: "",
        password: "",
        phone: "",
        focus_sector: "",
        role_in_business: "",
      },

      company: {
        name: "",
        legal_name: "",
      },

      errors: {},
      createdUser: null,
      createdCompany: null,
    };
  },

  computed: {
    progressPct() {
      if (this.phase === 'verify_email') return 30;
      return Math.round((this.step / this.totalSteps) * 100);
    },
    stepTitle() {
      if (this.phase === 'verify_email') return "Verificar correo";
      if (this.step === 1) return "Cuenta";
      if (this.step === 2) return "Perfil";
      return "Empresa";
    },
    maskedEmail() {
      const e = this.form.email || '';
      const [user, domain] = e.split('@');
      if (!domain) return e;
      return `${user[0]}${'*'.repeat(Math.min(user.length - 1, 3))}@${domain}`;
    },
    currentVersion() {
      return currentVersionString();
    },
  },

  methods: {
    clearErrors() {
      this.errors = {};
      this.errorMessage = null;
    },

    prevStep() {
      if (this.loading) return;
      this.step = Math.max(1, this.step - 1);
    },

    backToStep1() {
      this.phase = 'form';
      this.step = 1;
      this.verificationCode = '';
      this.clearErrors();
      // Reset captcha so user gets a fresh token
      this.$nextTick(() => this.resetTurnstile());
    },

    nextOrSubmit() {
      if (this.loading) return;
      this.clearErrors();

      const ok = this.validateStep(this.step);
      if (!ok) {
        this.toast?.("warning", "Revisa los campos marcados.");
        return;
      }

      // After step 1: verify CAPTCHA and send email code
      if (this.step === 1) {
        this.requestEmailCode();
        return;
      }

      if (this.step < this.totalSteps) this.step += 1;
      else this.submitAll();
    },

    validateEmail(email) {
      const v = String(email || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    },

    validateStep(step) {
      const e = {};

      if (step === 1) {
        if (!this.form.first_name) e.first_name = "El nombre es requerido.";
        if (!this.form.last_name) e.last_name = "El apellido es requerido.";

        if (!this.form.email) e.email = "El correo es requerido.";
        else if (!this.validateEmail(this.form.email)) e.email = "Correo inválido.";

        this.form.username = normalizeUsername(this.form.username);
        const usernameError = validateUsername(this.form.username);
        if (usernameError) e.username = usernameError;

        if (!this.form.password) e.password = "La contraseña es requerida.";
        else if (String(this.form.password).length < 8) e.password = "Mínimo 8 caracteres.";

        if (!this.form.phone) e.phone = "El teléfono es requerido.";
      }

      if (step === 2) {
        if (!this.form.focus_sector) e.focus_sector = "Selecciona un sector.";
        if (!this.form.role_in_business) e.role_in_business = "Selecciona tu cargo.";
      }

      if (step === 3) {
        if (!this.company.name) e.company_name = "El nombre del negocio es requerido.";
        if (!this.company.legal_name) e.company_legal_name = "La razón social es requerida.";
      }

      this.errors = e;
      return Object.keys(e).length === 0;
    },

    // ── CAPTCHA ─────────────────────────────────────────────────────────────

    loadTurnstile() {
      if (window.turnstile) { this.renderTurnstile(); return; }
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => this.renderTurnstile();
      document.head.appendChild(script);
    },

    renderTurnstile() {
      const el = this.$refs.turnstileEl;
      if (!el || !window.turnstile) return;

      const siteKey = window.__TURNSTILE_SITE_KEY__ || '1x00000000000000000000AA'; // test key fallback
      this._turnstileId = window.turnstile.render(el, {
        sitekey:  siteKey,
        theme:    'light',
        callback: (token) => { this.captchaToken = token; },
        'expired-callback': () => { this.captchaToken = null; },
        'error-callback':   () => { this.captchaToken = null; },
      });
    },

    resetTurnstile() {
      if (window.turnstile && this._turnstileId !== null) {
        window.turnstile.reset(this._turnstileId);
        this.captchaToken = null;
      } else {
        this.$nextTick(() => this.renderTurnstile());
      }
    },

    // ── EMAIL VERIFICATION ───────────────────────────────────────────────────

    async requestEmailCode() {
      const hasCaptchaKey = !!window.__TURNSTILE_SITE_KEY__;
      if (hasCaptchaKey && !this.captchaToken) {
        this.errors = { captcha: "Completa la verificación CAPTCHA." };
        return;
      }

      this.loading = true;
      try {
        await this.$axios.post('/register/send-code', {
          email:         this.form.email,
          first_name:    this.form.first_name,
          captcha_token: this.captchaToken || '',
        });

        this.phase = 'verify_email';
        this.verificationCode = '';
        this.startResendCooldown(60);
        this.$nextTick(() => this.$refs.codeInput?.focus());
      } catch (err) {
        const { errorMessage } = normalizeApiError(err);
        this.errorMessage = errorMessage;
        this.toast?.("error", errorMessage);
        this.resetTurnstile();
      } finally {
        this.loading = false;
      }
    },

    async submitCode() {
      this.clearErrors();
      if (this.verificationCode.length !== 6) {
        this.errors = { code: "Ingresa el código de 6 dígitos." };
        return;
      }

      this.loading = true;
      try {
        const { data } = await this.$axios.post('/register/verify-code', {
          email: this.form.email,
          code:  this.verificationCode,
        });

        this.verifiedToken = data.verified_token;
        this.phase = 'form';
        this.step = 2;
        this.toast?.("success", "Correo verificado. ¡Continúa tu registro!");
      } catch (err) {
        const { errorMessage } = normalizeApiError(err);
        this.errors = { code: errorMessage };
      } finally {
        this.loading = false;
      }
    },

    async resendCode() {
      if (this.resendCooldown > 0 || this.loading) return;
      await this.requestEmailCode();
    },

    startResendCooldown(seconds) {
      this.resendCooldown = seconds;
      clearInterval(this._resendTimer);
      this._resendTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) clearInterval(this._resendTimer);
      }, 1000);
    },

    // ── FINAL SUBMIT ─────────────────────────────────────────────────────────

    async submitAll() {
      this.loading = true;
      this.errorMessage = null;

      try {
        const payload = {
          data: {
            verified_token: this.verifiedToken,
            user: {
              first_name:       this.form.first_name,
              last_name:        this.form.last_name,
              email:            this.form.email,
              username:         this.form.username,
              password:         this.form.password,
              phone:            this.form.phone,
              focus_sector:     this.form.focus_sector,
              role_in_business: this.form.role_in_business,
            },
            company: {
              name:       this.company.name,
              legal_name: this.company.legal_name,
            },
          },
        };

        const { data } = await this.$axios.post(this.registerUrl, payload);

        this.createdUser    = data?.data?.user    || data?.user    || data?.data || null;
        this.createdCompany = data?.data?.company || data?.company || null;

        this.toast?.("success", "Cuenta creada correctamente.");
        window.location.href = '/app';
      } catch (err) {
        console.error("Register error:", err);
        const { errorMessage, fieldErrors } = normalizeApiError(err);
        this.errors = { ...this.errors, ...fieldErrors };
        this.errorMessage = errorMessage;
        this.toast?.("error", errorMessage);
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.loadTurnstile();
  },

  beforeUnmount() {
    clearInterval(this._resendTimer);
  },
};

</script>

<style scoped lang="scss">
.main-input {
  display: block;
  width: 100%;
  border-radius: 0.75rem;
  border-width: 1px;
  padding: 0.7rem 0.9rem;
  font-size: 0.875rem;
  outline: none;

  /* Light mode (matches AuthForm) */
  background: #ffffff;
  border-color: #e5e7eb;
  color: #111827;

  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &::placeholder {
    color: rgba(107, 114, 128, 0.7);
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(11, 128, 238, 0.20);
    border-color: rgba(11, 128, 238, 0.8);
  }

  option {
    background: #ffffff;
    color: #111827;
  }

  /* Dark mode */
  .dark & {
    background: #1E2025;
    border-color: rgba(255, 255, 255, 0.10);
    color: #f3f4f6;

    &::placeholder {
      color: rgba(156, 163, 175, 0.45);
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba(11, 128, 238, 0.25);
      border-color: rgba(11, 128, 238, 0.8);
    }

    option {
      background: #17181B;
      color: #f3f4f6;
    }
  }
}

.error {
  border-color: rgba(239, 68, 68, 0.7) !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
